import React, { useRef, useCallback, useMemo, useEffect, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { useTokenConfig } from '../../contexts/TokenConfigContext';

const NetworkGraph = ({ graph, farms, onNodeClick, selectedFarm }) => {
  const { tokenSymbol } = useTokenConfig();
  const graphRef = useRef();
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Color palette for clusters
  const farmColors = [
    '#E91E8C', '#9C27B0', '#3F51B5', '#06B6D4', '#10B981',
    '#F59E0B', '#EF4444', '#8B5CF6', '#0EA5E9', '#84CC16',
    '#F97316', '#EC4899', '#14B8A6', '#6366F1', '#22C55E'
  ];
  
  const COLORS = {
    unconnected: '#374151',
    unconnectedBorder: '#4B5563',
    background: '#0a1628',
    link: 'rgba(255, 255, 255, 0.3)',
    arrow: 'rgba(255, 255, 255, 0.5)'
  };
  
  const toFriendlyAddress = (addr) => {
    if (!addr) return '';
    if (addr.startsWith('EQ') || addr.startsWith('UQ') || addr.startsWith('kQ')) {
      return addr;
    }
    return addr;
  };
  
  const truncateAddress = (addr) => {
    if (!addr) return '';
    if (addr.length <= 12) return addr;
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };
  
  const openInTonViewer = (address) => {
    window.open(`https://tonviewer.com/${address}`, '_blank');
  };
  
  // Node sizes - HUGE range like in reference
  const getNodeSize = (balance, isConnected) => {
    if (!balance || balance <= 0) return isConnected ? 8 : 5;
    
    // Logarithmic scale with very wide range
    // Reference shows sizes from tiny (~5px) to massive (~200px)
    const logBalance = Math.log10(balance + 1);
    
    // Scale: 1K=15, 10K=25, 100K=40, 1M=60, 10M=100, 100M=150
    const size = Math.pow(logBalance, 1.8) * 3;
    
    const minSize = isConnected ? 8 : 5;
    const maxSize = 180; // Allow very large nodes
    
    return Math.max(minSize, Math.min(maxSize, size));
  };

  // Calculate FIXED positions for all nodes
  const graphData = useMemo(() => {
    if (!graph || !graph.nodes) return { nodes: [], links: [] };
    
    const connectedIds = new Set();
    graph.links.forEach(link => {
      connectedIds.add(typeof link.source === 'object' ? link.source.id : link.source);
      connectedIds.add(typeof link.target === 'object' ? link.target.id : link.target);
    });
    
    // Group nodes by cluster
    const clusters = {};
    const unconnectedNodes = [];
    
    graph.nodes.forEach(node => {
      const isConnected = connectedIds.has(node.id);
      if (isConnected && node.farm_id) {
        if (!clusters[node.farm_id]) clusters[node.farm_id] = [];
        clusters[node.farm_id].push(node);
      } else if (isConnected) {
        if (!clusters['misc']) clusters['misc'] = [];
        clusters['misc'].push(node);
      } else {
        unconnectedNodes.push(node);
      }
    });
    
    const clusterIds = Object.keys(clusters).sort((a, b) => {
      return clusters[b].length - clusters[a].length;
    });
    
    const PADDING = 35; // Gap between node edges
    const CLUSTER_GAP = 100; // Minimum gap between clusters
    
    // Рассчитываем предполагаемый радиус каждого кластера
    const clusterRadii = {};
    clusterIds.forEach(cid => {
      const clusterNodes = clusters[cid];
      const numNodes = clusterNodes.length;
      // Оценка размеров нод в кластере
      const sizes = clusterNodes.map(n => getNodeSize(n.balance, true));
      const avgSize = sizes.reduce((a, b) => a + b, 0) / sizes.length;
      const maxSize = Math.max(...sizes);
      // Радиус = достаточно для размещения всех нод по спирали
      const estimatedRadius = Math.max(100, numNodes * (avgSize + PADDING) / (2 * Math.PI) + maxSize * 2);
      clusterRadii[cid] = estimatedRadius;
    });
    
    // Размещаем кластеры так, чтобы они не пересекались
    const clusterCenters = {};
    const placedClusters = []; // {x, y, radius}
    
    clusterIds.forEach((cid, index) => {
      const myRadius = clusterRadii[cid];
      
      if (index === 0) {
        // Первый (самый большой) кластер в центре
        clusterCenters[cid] = { x: 0, y: 0 };
        placedClusters.push({ x: 0, y: 0, radius: myRadius });
      } else {
        // Ищем позицию для следующего кластера
        let bestPos = null;
        let angle = 0;
        let searchRadius = 0;
        
        // Начинаем с минимального расстояния от центра
        const firstClusterRadius = clusterRadii[clusterIds[0]] || 200;
        searchRadius = firstClusterRadius + myRadius + CLUSTER_GAP;
        
        // Спиральный поиск свободного места
        for (let attempt = 0; attempt < 100; attempt++) {
          const x = Math.cos(angle) * searchRadius;
          const y = Math.sin(angle) * searchRadius;
          
          // Проверяем пересечение с уже размещенными кластерами
          let hasOverlap = false;
          for (const placed of placedClusters) {
            const dist = Math.sqrt((x - placed.x) ** 2 + (y - placed.y) ** 2);
            const minDist = myRadius + placed.radius + CLUSTER_GAP;
            if (dist < minDist) {
              hasOverlap = true;
              break;
            }
          }
          
          if (!hasOverlap) {
            bestPos = { x, y };
            break;
          }
          
          // Следующая позиция по спирали
          angle += Math.PI / 6; // 30 градусов
          if (angle >= Math.PI * 2) {
            angle = 0;
            searchRadius += myRadius + CLUSTER_GAP;
          }
        }
        
        if (!bestPos) {
          // Fallback - просто размещаем дальше
          bestPos = { 
            x: Math.cos(index * 0.8) * (searchRadius + index * 200), 
            y: Math.sin(index * 0.8) * (searchRadius + index * 200) 
          };
        }
        
        clusterCenters[cid] = bestPos;
        placedClusters.push({ x: bestPos.x, y: bestPos.y, radius: myRadius });
      }
    });
    
    const nodes = [];
    
    // Helper: check if two circles overlap
    const checkOverlap = (x1, y1, r1, x2, y2, r2, minGap = PADDING) => {
      const dx = x2 - x1;
      const dy = y2 - y1;
      const distance = Math.sqrt(dx * dx + dy * dy);
      return distance < (r1 + r2 + minGap);
    };
    
    // Helper: find position that doesn't overlap
    const findNonOverlappingPosition = (centerX, centerY, size, placedNodes, startRadius = 0, clusterSize = 1) => {
      // Увеличиваем начальный радиус для больших кластеров
      const baseRadius = Math.max(startRadius, clusterSize * 15);
      let radius = baseRadius;
      let angle = -Math.PI / 2;
      
      // Более мелкий шаг угла для точного размещения
      const angleStep = Math.PI / 24; // 7.5 degree steps
      // Больший шаг радиуса
      const radiusStep = size + PADDING;
      let attempts = 0;
      const maxAttempts = 500;
      
      while (attempts < maxAttempts) {
        const x = centerX + Math.cos(angle) * radius;
        const y = centerY + Math.sin(angle) * radius;
        
        // Check overlap with all placed nodes
        let hasOverlap = false;
        for (const placed of placedNodes) {
          if (checkOverlap(x, y, size, placed.x, placed.y, placed.size)) {
            hasOverlap = true;
            break;
          }
        }
        
        if (!hasOverlap) {
          return { x, y };
        }
        
        // Try next position - spiral outward
        angle += angleStep;
        if (angle >= Math.PI * 2 - Math.PI / 2) {
          angle = -Math.PI / 2;
          radius += radiusStep;
        }
        attempts++;
      }
      
      // Fallback: place further out
      return { 
        x: centerX + Math.cos(angle) * (radius + size * 2), 
        y: centerY + Math.sin(angle) * (radius + size * 2) 
      };
    };
    
    // Position clustered nodes with collision detection
    clusterIds.forEach(cid => {
      const clusterNodes = clusters[cid];
      const center = clusterCenters[cid];
      
      // Sort by balance descending - place big ones first
      clusterNodes.sort((a, b) => (b.balance || 0) - (a.balance || 0));
      
      // Get cluster color
      const farmIndex = cid === 'misc' ? 0 : (parseInt(cid) - 1);
      const clusterColor = farmColors[farmIndex % farmColors.length];
      
      // Track placed nodes in this cluster
      const clusterPlacedNodes = [];
      
      const numNodesInCluster = clusterNodes.length;
      
      clusterNodes.forEach((node, index) => {
        const size = getNodeSize(node.balance, true);
        const friendlyAddr = toFriendlyAddress(node.id);
        
        let x, y;
        if (index === 0) {
          // First (largest) node at center
          x = center.x;
          y = center.y;
        } else {
          // Find non-overlapping position - pass cluster size for better spacing
          const startRadius = size + (clusterPlacedNodes[0]?.size || 50) + PADDING * 2;
          const pos = findNonOverlappingPosition(center.x, center.y, size, clusterPlacedNodes, startRadius, numNodesInCluster);
          x = pos.x;
          y = pos.y;
        }
        
        const nodeData = {
          ...node,
          friendlyAddress: friendlyAddr,
          label: truncateAddress(friendlyAddr),
          size: size,
          isConnected: true,
          color: clusterColor,
          highlighted: selectedFarm && node.farm_id === selectedFarm,
          x: x,
          y: y,
          fx: x,
          fy: y
        };
        
        nodes.push(nodeData);
        clusterPlacedNodes.push(nodeData);
      });
    });
    
    // Unconnected nodes in outer ring
    const clusterExtents = Object.values(clusterCenters).map(c => Math.sqrt(c.x * c.x + c.y * c.y));
    const maxClusterExtent = clusterExtents.length > 0 
      ? Math.max(...clusterExtents) + 300 
      : 150;
    
    if (unconnectedNodes.length > 0) {
      // Sort by balance descending - place bigger ones first
      const sorted = [...unconnectedNodes].sort((a, b) => (b.balance || 0) - (a.balance || 0));
      
      // Scatter around outer area with collision detection
      const minRadius = maxClusterExtent + 100;
      
      // Seeded random for consistent layout
      const seededRandom = (seed) => {
        const x = Math.sin(seed * 12.9898) * 43758.5453;
        return x - Math.floor(x);
      };
      
      // Track placed unconnected nodes
      const unconnectedPlaced = [];
      
      sorted.forEach((node, index) => {
        const size = getNodeSize(node.balance, false);
        const friendlyAddr = toFriendlyAddress(node.id);
        
        // Try to find non-overlapping position
        let x, y;
        let placed = false;
        let radius = minRadius + seededRandom(index * 13) * 200;
        let angle = seededRandom(index * 7) * Math.PI * 2;
        
        for (let attempt = 0; attempt < 100 && !placed; attempt++) {
          // Add some randomness
          const testX = Math.cos(angle) * radius + (seededRandom(index * 17 + attempt) - 0.5) * 60;
          const testY = Math.sin(angle) * radius + (seededRandom(index * 23 + attempt) - 0.5) * 60;
          
          // Check overlap with all placed nodes (clusters + unconnected)
          let hasOverlap = false;
          for (const placed of [...nodes, ...unconnectedPlaced]) {
            if (checkOverlap(testX, testY, size, placed.x, placed.y, placed.size, PADDING)) {
              hasOverlap = true;
              break;
            }
          }
          
          if (!hasOverlap) {
            x = testX;
            y = testY;
            placed = true;
          } else {
            // Try different angle and radius
            angle += Math.PI / 8;
            if (angle > Math.PI * 2) {
              angle -= Math.PI * 2;
              radius += size + PADDING;
            }
          }
        }
        
        // Fallback if no position found
        if (!placed) {
          x = Math.cos(angle) * radius;
          y = Math.sin(angle) * radius;
        }
        
        const nodeData = {
          ...node,
          friendlyAddress: friendlyAddr,
          label: truncateAddress(friendlyAddr),
          size: size,
          isConnected: false,
          color: COLORS.unconnected,
          highlighted: false,
          x: x,
          y: y,
          fx: x,
          fy: y
        };
        
        nodes.push(nodeData);
        unconnectedPlaced.push(nodeData);
      });
    }
    
    const links = graph.links.map(link => ({ ...link }));
    
    return { nodes, links };
  }, [graph, selectedFarm]);
  
  // Node rendering - transparent bubbles with glow
  const nodeCanvasObject = useCallback((node, ctx, globalScale) => {
    const size = node.size || 10;
    const x = node.x;
    const y = node.y;
    
    if (!isFinite(x) || !isFinite(y)) return;
    
    const isConnected = node.isConnected;
    const baseColor = node.color || COLORS.unconnected;
    const glowColor = isConnected ? lightenColor(baseColor, 30) : COLORS.unconnectedBorder;
    
    // Outer glow
    const glowSize = size * 1.4;
    const glowGradient = ctx.createRadialGradient(x, y, size * 0.8, x, y, glowSize);
    glowGradient.addColorStop(0, glowColor + '30');
    glowGradient.addColorStop(1, 'transparent');
    ctx.beginPath();
    ctx.arc(x, y, glowSize, 0, 2 * Math.PI);
    ctx.fillStyle = glowGradient;
    ctx.fill();
    
    // Main bubble - transparent with gradient
    const bubbleGradient = ctx.createRadialGradient(
      x - size * 0.3, y - size * 0.3, 0,  // Light spot offset
      x, y, size
    );
    bubbleGradient.addColorStop(0, baseColor + '60');  // Lighter center
    bubbleGradient.addColorStop(0.5, baseColor + '40');
    bubbleGradient.addColorStop(1, baseColor + '20');   // Transparent edge
    
    ctx.beginPath();
    ctx.arc(x, y, size, 0, 2 * Math.PI);
    ctx.fillStyle = bubbleGradient;
    ctx.fill();
    
    // Border ring
    ctx.beginPath();
    ctx.arc(x, y, size, 0, 2 * Math.PI);
    ctx.strokeStyle = isConnected ? glowColor : COLORS.unconnectedBorder;
    ctx.lineWidth = isConnected ? 2 : 1.5;
    ctx.stroke();
    
    // Highlight ring for selected
    if (node.highlighted) {
      ctx.beginPath();
      ctx.arc(x, y, size + 5, 0, 2 * Math.PI);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      ctx.setLineDash([5, 3]);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    
    // OUR wallet indicator
    if (node.is_our) {
      ctx.beginPath();
      ctx.arc(x + size * 0.7, y - size * 0.7, Math.max(4, size * 0.15), 0, 2 * Math.PI);
      ctx.fillStyle = '#22c55e';
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
    
    // Balance text inside (only for larger nodes)
    if (size > 25) {
      const balanceText = formatBalance(node.balance);
      const fontSize = Math.min(size * 0.3, 14);
      ctx.font = `bold ${fontSize}px -apple-system, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fillText(balanceText, x, y);
    }
    
    // Address label (only for connected and zoomed in)
    if (isConnected && size > 15 && globalScale > 0.5) {
      const fontSize = Math.max(8, Math.min(11, 12 / globalScale));
      ctx.font = `500 ${fontSize}px -apple-system, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.fillText(node.label, x, y + size + 8);
    }
  }, []);
  
  // Link rendering - thin white lines with arrows
  const linkCanvasObject = useCallback((link, ctx, globalScale) => {
    const start = link.source;
    const end = link.target;
    
    if (typeof start !== 'object' || typeof end !== 'object') return;
    if (!isFinite(start.x) || !isFinite(start.y) || !isFinite(end.x) || !isFinite(end.y)) return;
    
    const startSize = start.size || 10;
    const endSize = end.size || 10;
    
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist === 0) return;
    
    const nx = dx / dist;
    const ny = dy / dist;
    
    const sx = start.x + nx * (startSize + 3);
    const sy = start.y + ny * (startSize + 3);
    const ex = end.x - nx * (endSize + 8);
    const ey = end.y - ny * (endSize + 8);
    
    // Straight line
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(ex, ey);
    ctx.strokeStyle = COLORS.link;
    ctx.lineWidth = 1;
    ctx.stroke();
    
    // Arrow head
    const arrowSize = 5;
    const angle = Math.atan2(dy, dx);
    
    ctx.beginPath();
    ctx.moveTo(ex, ey);
    ctx.lineTo(
      ex - arrowSize * Math.cos(angle - Math.PI / 7),
      ey - arrowSize * Math.sin(angle - Math.PI / 7)
    );
    ctx.lineTo(
      ex - arrowSize * Math.cos(angle + Math.PI / 7),
      ey - arrowSize * Math.sin(angle + Math.PI / 7)
    );
    ctx.closePath();
    ctx.fillStyle = COLORS.arrow;
    ctx.fill();
  }, []);
  
  const handleNodeClick = useCallback((node) => {
    if (onNodeClick) onNodeClick(node);
    openInTonViewer(node.id);
  }, [onNodeClick]);
  
  // Define pointer area to match visual node size (fixes hover targeting)
  const nodePointerAreaPaint = useCallback((node, color, ctx) => {
    const size = node.size || 10;
    ctx.beginPath();
    ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();
  }, []);
  
  useEffect(() => {
    if (graphRef.current && graphData.nodes.length > 0 && !hasInitialized) {
      const timer = setTimeout(() => {
        graphRef.current?.zoomToFit(400, 100);
        setHasInitialized(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [graphData.nodes.length, hasInitialized]);
  
  useEffect(() => {
    setHasInitialized(false);
  }, [graph]);
  
  if (!graphData.nodes.length) {
    return (
      <div className="flex items-center justify-center h-[400px]" style={{ background: COLORS.background }}>
        <div className="text-center">
          <div className="text-5xl mb-3">🔍</div>
          <p className="text-gray-400 text-sm">Нет связей</p>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className="overflow-hidden relative"
      style={{ 
        height: 'min(650px, 75vh)',
        minHeight: 450,
        background: `radial-gradient(ellipse at center, #0f172a 0%, ${COLORS.background} 100%)`
      }}
    >
      <ForceGraph2D
        ref={graphRef}
        graphData={graphData}
        nodeCanvasObject={nodeCanvasObject}
        nodePointerAreaPaint={nodePointerAreaPaint}
        linkCanvasObject={linkCanvasObject}
        onNodeClick={handleNodeClick}
        nodeLabel={node => `${node.friendlyAddress}\n${formatBalance(node.balance)} ${tokenSymbol || 'TOKEN'}\n\nКлик → TonViewer`}
        backgroundColor="transparent"
        d3AlphaDecay={1}
        d3VelocityDecay={1}
        cooldownTicks={0}
        warmupTicks={0}
        enableNodeDrag={false}
        enableZoomInteraction={true}
        enablePanInteraction={true}
      />
      
      <div className="absolute bottom-3 left-3 text-[11px] text-gray-500 bg-black/40 px-3 py-1.5 rounded-lg backdrop-blur-sm">
        Scroll → Zoom | Drag → Pan | Click → TonViewer
      </div>
    </div>
  );
};

function formatBalance(balance) {
  if (!balance) return '0';
  if (balance >= 1e9) return `${(balance / 1e9).toFixed(1)}B`;
  if (balance >= 1e6) return `${(balance / 1e6).toFixed(1)}M`;
  if (balance >= 1e3) return `${(balance / 1e3).toFixed(1)}K`;
  return balance.toFixed(0);
}

function lightenColor(hex, percent) {
  if (!hex || !hex.startsWith('#')) return hex;
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, (num >> 16) + amt);
  const G = Math.min(255, ((num >> 8) & 0x00FF) + amt);
  const B = Math.min(255, (num & 0x0000FF) + amt);
  return `#${((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1)}`;
}

export default NetworkGraph;
